import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getInitials } from "../Helpers/stringHelpers";
import {
  updateUserDetails,
  deleteAllChatHistory,
  sendVerificationOtp,
  confirmVerificationOtp,
} from "../services/authenticationServices";
import { AuthContext } from "../Hook/AuthProvider";
import "../styles/Profile.css";
import Header from "./Header";
import PolicyModal from "./PolicyModal";

const MAX_AGE = 65;

const getAgeError = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  if (numeric > MAX_AGE) {
    return `Age limit crossed. Maximum allowed age is ${MAX_AGE}.`;
  }
  return "";
};

const SettingsPage = ({
  Check,
  isSidebarColl,
  setIsSidebarColl,
  setRefreshHistory,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [deleteChats, setDeleteChats] = useState(false);
  const [delclick, setDelClick] = useState(false);
  const [policyModal, setPolicyModal] = useState({ isOpen: false, type: "privacy" });
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [age, setAge] = useState("");
  const [ageError, setAgeError] = useState("");
  const [gender, setGender] = useState("");
  const [phone_number, setPhone_number] = useState("");
  const [email, setEmail] = useState("");
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState({
    isOpen: false,
    type: null,
    value: "",
    otp: "",
    loading: false,
    error: "",
  });
  const {
    logout,
    keycloak,
    isAuthenticated,
    profileData,
    setProfileData,
    fetchProfile,
  } = useContext(AuthContext);
  const [deletingChats, setDeletingChats] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleOpen = () => setDelClick(true);
  const handleClose = () => setDelClick(false);

  useEffect(() => {
    if (delclick || deleteChats) {
      document.body.classList.add("profile-modal-blur-active");
    } else {
      document.body.classList.remove("profile-modal-blur-active");
    }

    return () => {
      document.body.classList.remove("profile-modal-blur-active");
    };
  }, [delclick, deleteChats]);

  const editButtonClickHandler = () => {
    setErrorMessage("");
    setAgeError("");
    setIsEditing(true);
  };

  const cleanPhone = (val) => {
    if (!val) return "";
    return String(val).replace(/^\+91/, "").replace(/\D/g, "").slice(0, 10);
  };

  const sanitizeName = (val) => {
    return val.replace(/[^a-zA-Z\s]/g, "").slice(0, 30);
  };

  const handleFirstNameChange = (e) => {
    setFirst_name(sanitizeName(e.target.value));
  };

  const handleLastNameChange = (e) => {
    setLast_name(sanitizeName(e.target.value));
  };

  const handleNameBlur = (setter) => {
    setter((prev) =>
      prev
        ? prev
            .trim()
            .split(/\s+/)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(" ")
        : ""
    );
  };

  const handleAgeChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    setAge(digits);
    setAgeError(getAgeError(digits));
  };

  // Update local state when profileData changes
  useEffect(() => {
    if (profileData) {
      setFirst_name(profileData.first_name || "");
      setLast_name(profileData.last_name || "");
      setAge(profileData.age || "");
      setGender(profileData.gender || "");
      setPhone_number(cleanPhone(profileData.phone_number));
      setEmail(profileData.email || "");
    }
  }, [profileData]);

  // Initial profile load
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await fetchProfile();
        if (response) {
          setFirst_name(response.first_name || "");
          setLast_name(response.last_name || "");
          setAge(response.age || "");
          setGender(response.gender || "");
          setPhone_number(cleanPhone(response.phone_number));
          setEmail(response.email || "");
        }
      } catch (err) {
        console.error("Error loading profile:", err);
      }
    };

    if (!profileData) {
      loadProfile();
    }
  }, []);

  const handleSubmit = async () => {
    setErrorMessage("");
    const ageValidationMessage = getAgeError(age);
    if (ageValidationMessage) {
      setAgeError(ageValidationMessage);
      return;
    }
    setAgeError("");
    const trimmedFirst = first_name.trim();
    const trimmedLast = last_name.trim();
    const trimmedPhone = phone_number.trim();
    const trimmedEmail = email.trim();

    // Check if user is adding phone number for the first time
    const isAddingPhone = !profileData?.phone_number && trimmedPhone !== "";
    if (isAddingPhone) {
      if (trimmedPhone.length !== 10 || !/^[6-9]/.test(trimmedPhone)) {
        setErrorMessage("Please enter a valid 10-digit mobile number starting with 6-9");
        return;
      }
      setLoading(true);
      try {
        await sendVerificationOtp("phone", trimmedPhone);
        setOtpModal({
          isOpen: true,
          type: "phone",
          value: trimmedPhone,
          otp: "",
          loading: false,
          error: "",
        });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Failed to send verification OTP";
        setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Check if user is adding email for the first time
    const isAddingEmail = !profileData?.email && trimmedEmail !== "";
    if (isAddingEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setErrorMessage("Please enter a valid email address");
        return;
      }
      setLoading(true);
      try {
        await sendVerificationOtp("email", trimmedEmail);
        setOtpModal({
          isOpen: true,
          type: "email",
          value: trimmedEmail,
          otp: "",
          loading: false,
          error: "",
        });
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || "Failed to send verification OTP";
        setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Standard profile details update
    setLoading(true);
    const phoneToSend = trimmedPhone ? `+91${trimmedPhone}` : null;

    updateUserDetails(
      trimmedFirst,
      trimmedLast,
      trimmedEmail,
      gender,
      age ? parseInt(age, 10) : null,
      phoneToSend,
    )
      .then(() => {
        return fetchProfile();
      })
      .then((profileResponse) => {
        if (profileResponse) {
          setFirst_name(profileResponse.first_name || "");
          setLast_name(profileResponse.last_name || "");
          setAge(profileResponse.age || "");
          setGender(profileResponse.gender || "");
          setEmail(profileResponse.email || "");
          setPhone_number(cleanPhone(profileResponse.phone_number));
        }
        setIsEditing(false);
      })
      .catch((e) => {
        console.error("Error updating profile:", e);
        const errDetail = e?.response?.data?.detail;
        const msg =
          typeof errDetail === "string"
            ? errDetail
            : e?.message || "Failed to update profile. Please try again.";
        setErrorMessage(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleVerifyOtp = async () => {
    if (!otpModal.otp || otpModal.otp.trim().length !== 6) {
      setOtpModal((prev) => ({ ...prev, error: "Please enter the 6-digit OTP code" }));
      return;
    }
    const ageValidationMessage = getAgeError(age);
    if (ageValidationMessage) {
      setAgeError(ageValidationMessage);
      setOtpModal((prev) => ({ ...prev, error: ageValidationMessage }));
      return;
    }
    setAgeError("");
    setOtpModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await confirmVerificationOtp(otpModal.type, otpModal.value, otpModal.otp.trim());

      // Save other pending profile fields
      await updateUserDetails(
        first_name.trim(),
        last_name.trim(),
        otpModal.type === "email" ? otpModal.value : email.trim(),
        gender,
        age ? parseInt(age, 10) : null,
        otpModal.type === "phone" ? `+91${otpModal.value}` : (phone_number.trim() ? `+91${phone_number.trim()}` : null),
      );

      await fetchProfile();
      setOtpModal({ isOpen: false, type: null, value: "", otp: "", loading: false, error: "" });
      setIsEditing(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Invalid OTP code. Please try again.";
      setOtpModal((prev) => ({
        ...prev,
        loading: false,
        error: typeof msg === "string" ? msg : JSON.stringify(msg),
      }));
    }
  };

  const handleResendOtp = async () => {
    setOtpModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await sendVerificationOtp(otpModal.type, otpModal.value);
      setOtpModal((prev) => ({ ...prev, loading: false, error: "New OTP code sent!" }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to resend OTP";
      setOtpModal((prev) => ({
        ...prev,
        loading: false,
        error: typeof msg === "string" ? msg : JSON.stringify(msg),
      }));
    }
  };

  const handleCancel = () => {
    setErrorMessage("");
    setAgeError("");
    if (profileData) {
      setFirst_name(profileData.first_name || "");
      setLast_name(profileData.last_name || "");
      setAge(profileData.age || "");
      setGender(profileData.gender || "");
      setPhone_number(cleanPhone(profileData.phone_number));
      setEmail(profileData.email || "");
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    setProfileData(null);
    try {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("hasCompletedOnboarding");
      localStorage.removeItem("hastour");

      if (logout) {
        logout();
      }
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
      if (logout) {
        logout();
      }
      navigate("/");
    }
  };

  const handleDeleteAllChats = async () => {
    setDeletingChats(true);
    try {
      await deleteAllChatHistory();
    } catch (error) {
      console.error("Failed to delete chat history:", error);
    } finally {
      setDeletingChats(false);
      if (setRefreshHistory) setRefreshHistory();
    }
  };

  const isPro = profileData?.user_type === "pro";
  const isEmailVerified = !!(profileData?.is_email_verified || (profileData?.email && !profileData?.phone_number));
  const isPhoneVerified = !!(profileData?.is_phone_verified || (profileData?.phone_number && !profileData?.email));
  const userInitials =
    profileData?.first_name && profileData?.last_name
      ? getInitials(
          `${profileData.first_name.trim()} ${profileData.last_name.trim()}`,
        )
      : profileData?.first_name
        ? profileData.first_name.charAt(0).toUpperCase()
        : "U";

  return (
    <div style={{ width: "100%" }}>
      <div className="header-biling">
        <Header message={[{ message: `Settings/Profile` }]} flag={false} />
      </div>

      <div className={`profile-container ${isSidebarColl ? "" : ""}`}>
        <div className="profile-section">
          {/* Mobile Menu Icon */}
          <div className="mobile-menu-icon d-block d-md-none">
            <img
              src="/assets/images/Vector2.svg"
              alt="Menu"
              onClick={() => setIsSidebarColl(!isSidebarColl)}
              style={{ cursor: "pointer" }}
            />
          </div>

          {profileData ? (
            <div className="profile-content-wrap">
              {/* 1. Profile Hero Identity Banner */}
              <div className="profile-hero-card">
                <div className="profile-hero-left">
                  <div className="profile-hero-avatar">
                    <span>{userInitials}</span>
                  </div>
                  <div className="profile-hero-info">
                    <div className="profile-hero-name-row">
                      <h2 className="profile-hero-name">
                        {first_name || last_name
                          ? `${first_name} ${last_name}`.trim()
                          : "User Profile"}
                      </h2>
                      {isPro && (
                        <img
                          src="/assets/images/pro-button-gradient-1.svg"
                          alt="Pro"
                          className="profile-pro-tag-img"
                        />
                      )}
                    </div>
                    <div className="profile-hero-meta">
                      {email && <span>{email}</span>}
                      {phone_number && (
                        <>
                          <span className="profile-meta-dot">•</span>
                          <span>+91 {phone_number}</span>
                        </>
                      )}
                      {profileData?.referral_code && (
                        <>
                          <span className="profile-meta-dot">•</span>
                          <span className="profile-ref-code" title="Your Referral Code">
                            Code: <strong>{profileData.referral_code}</strong>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="profile-hero-actions">
                  {!isEditing ? (
                    <button
                      className="profile-btn-edit"
                      onClick={editButtonClickHandler}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      Edit Profile
                    </button>
                  ) : (
                    <button
                      className="profile-btn-cancel-top"
                      onClick={() => {
                        setIsEditing(false);
                        handleCancel();
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  )}
                  <button onClick={handleLogout} className="profile-btn-logout">
                    <img src="/assets/images/logout.svg" alt="Logout" />
                    Sign Out
                  </button>
                </div>
              </div>

              {/* 2. Personal Information Card */}
              <div className="profile-card">
                <div className="profile-card-header">
                  <div className="profile-card-title">
                    <img
                      src="/assets/images/user-icon.svg"
                      alt="Personal Information"
                      style={{ width: "18px", height: "18px" }}
                    />
                    <span>Personal Information</span>
                  </div>
                </div>

                {!isEditing ? (
                  <div className="profile-flat-grid">
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">First Name</span>
                      <span className="profile-flat-value">
                        {first_name || "—"}
                      </span>
                    </div>
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">Last Name</span>
                      <span className="profile-flat-value">
                        {last_name || "—"}
                      </span>
                    </div>
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">Gender</span>
                      <span className="profile-flat-value text-capitalize">
                        {gender || "—"}
                      </span>
                    </div>
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">Age</span>
                      <span className="profile-flat-value">
                        {age ? `${age} yrs` : "—"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="profile-form-grid">
                    <div className="profile-form-group">
                      <label
                        htmlFor="inputFirstName"
                        className="profile-form-label"
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        className="profile-form-input"
                        id="inputFirstName"
                        value={first_name}
                        onChange={handleFirstNameChange}
                        onBlur={() => handleNameBlur(setFirst_name)}
                        placeholder="Enter first name"
                        maxLength={30}
                      />
                    </div>
                    <div className="profile-form-group">
                      <label
                        htmlFor="inputLastName"
                        className="profile-form-label"
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        className="profile-form-input"
                        id="inputLastName"
                        value={last_name}
                        onChange={handleLastNameChange}
                        onBlur={() => handleNameBlur(setLast_name)}
                        placeholder="Enter last name"
                        maxLength={30}
                      />
                    </div>
                    <div className="profile-form-group">
                      <label className="profile-form-label">Gender</label>
                      <div className="profile-gender-pill-group">
                        {["male", "female", "other"].map((option) => (
                          <label
                            key={option}
                            className={`profile-gender-pill ${
                              gender === option ? "selected" : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="gender"
                              value={option}
                              checked={gender === option}
                              onChange={(e) => setGender(e.target.value)}
                              className="d-none"
                            />
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="profile-form-group">
                      <label htmlFor="inputAge" className="profile-form-label">
                        Age
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete="off"
                        className={`profile-form-input ${
                          ageError ? "profile-form-input-error" : ""
                        }`}
                        id="inputAge"
                        value={age}
                        onChange={handleAgeChange}
                        onBlur={() => setAgeError(getAgeError(age))}
                        placeholder="e.g. 25"
                        maxLength={2}
                        aria-invalid={!!ageError}
                        aria-describedby={ageError ? "inputAgeError" : undefined}
                      />
                      {ageError && (
                        <span
                          id="inputAgeError"
                          className="profile-form-error-text"
                          role="alert"
                        >
                          {ageError}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Contact Details Card */}
              <div className="profile-card">
                <div className="profile-card-header">
                  <div className="profile-card-title">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#0284c7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span>Contact Information</span>
                  </div>
                </div>

                {!isEditing ? (
                  <div className="profile-flat-grid profile-flat-contact-grid">
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">Email Address</span>
                      <div className="profile-email-value-wrap">
                        <span className="profile-flat-value">
                          {email || "—"}
                        </span>
                        {email && isEmailVerified && (
                          <span className="profile-verified-badge">
                            ✓ Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="profile-flat-item">
                      <span className="profile-flat-label">Phone Number</span>
                      <div className="profile-email-value-wrap">
                        <span className="profile-flat-value">
                          {phone_number && phone_number.trim() !== ""
                            ? `+91 ${phone_number.trim()}`
                            : "—"}
                        </span>
                        {phone_number && isPhoneVerified && (
                          <span className="profile-verified-badge">
                            ✓ Verified
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="profile-form-grid">
                    <div className="profile-form-group">
                      <label
                        htmlFor="inputEmail"
                        className="profile-form-label"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        className={`profile-form-input ${
                          profileData?.email && isEmailVerified
                            ? "profile-form-readonly"
                            : ""
                        }`}
                        id="inputEmail"
                        value={email}
                        readOnly={!!(profileData?.email && isEmailVerified)}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={
                          profileData?.email
                            ? ""
                            : "Enter email (OTP verification required)"
                        }
                        title={
                          profileData?.email && isEmailVerified
                            ? "Verified email cannot be changed"
                            : ""
                        }
                      />
                    </div>

                    <div className="profile-form-group">
                      <label
                        htmlFor="inputMobile"
                        className="profile-form-label"
                      >
                        Mobile Number
                      </label>
                      <div className="profile-phone-input-wrap">
                        <span className="profile-country-code">+91</span>
                        <input
                          type="text"
                          className={`profile-form-input profile-phone-input ${
                            profileData?.phone_number && isPhoneVerified
                              ? "profile-form-readonly"
                              : ""
                          }`}
                          id="inputMobile"
                          value={phone_number}
                          readOnly={!!(profileData?.phone_number && isPhoneVerified)}
                          onChange={(e) => {
                            const value = e.target.value
                              .replace("+91", "")
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            setPhone_number(value);
                          }}
                          maxLength={10}
                          placeholder={
                            profileData?.phone_number
                              ? ""
                              : "10-digit mobile (OTP verification required)"
                          }
                          title={
                            profileData?.phone_number && isPhoneVerified
                              ? "Verified phone number cannot be changed"
                              : ""
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Action Bar */}
                {isEditing && (
                  <div className="profile-edit-footer-wrap">
                    {errorMessage && (
                      <div className="profile-error-alert">{errorMessage}</div>
                    )}
                    <div className="profile-edit-footer">
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          handleCancel();
                        }}
                        className="profile-btn-secondary"
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        className="profile-btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Saving...</span>
                          </div>
                        ) : (
                          "Save Changes"
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Legal & Privacy policies */}
              <div className="profile-card">
                <div className="profile-card-header">
                  <div className="profile-card-title">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#475569"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                    <span>Legal & Policies</span>
                  </div>
                </div>

                <div className="profile-danger-item">
                  <div className="profile-danger-desc">
                    <h5 className="profile-danger-title" style={{ color: "#1e293b" }}>Privacy Policy</h5>
                    <p className="profile-danger-text">
                      Read how your data is handled and protected on Yooki.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="profile-btn-secondary"
                    onClick={() => setPolicyModal({ isOpen: true, type: "privacy" })}
                  >
                    View Policy
                  </button>
                </div>

                <div className="profile-danger-item" style={{ borderBottom: "none" }}>
                  <div className="profile-danger-desc">
                    <h5 className="profile-danger-title" style={{ color: "#1e293b" }}>Terms & Conditions</h5>
                    <p className="profile-danger-text">
                      Review terms of service, platform usage, and user rights.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="profile-btn-secondary"
                    onClick={() => setPolicyModal({ isOpen: true, type: "tnc" })}
                  >
                    View Terms
                  </button>
                </div>
              </div>

              {/* 4. Data & Privacy (Danger Zone) */}
              <div className="profile-card profile-card-danger">
                <div className="profile-card-header">
                  <div className="profile-card-title text-danger">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#dc2626"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    <span>Data & Privacy</span>
                  </div>
                </div>

                <div className="profile-danger-item">
                  <div className="profile-danger-desc">
                    <h5 className="profile-danger-title">
                      Delete all chat history
                    </h5>
                    <p className="profile-danger-text">
                      Permanently delete all your conversations and evaluations
                      with Yooki. This action cannot be undone.
                    </p>
                  </div>
                  <button
                    className="profile-btn-danger-outline"
                    onClick={() => setDeleteChats(true)}
                    disabled={deletingChats}
                  >
                    {deletingChats ? (
                      <div className="loader-class-buttonclick"></div>
                    ) : (
                      "Delete Chats"
                    )}
                  </button>
                </div>

                <div
                  className="profile-danger-item"
                  style={{ borderBottom: "none" }}
                >
                  <div className="profile-danger-desc">
                    <h5 className="profile-danger-title">Deactivate Account</h5>
                    <p className="profile-danger-text">
                      Permanently remove your profile and all associated data from
                      Yooki.
                    </p>
                  </div>
                  <button
                    className="profile-btn-danger"
                    onClick={() => setDelClick(true)}
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="profile-loading-skeleton">
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            </div>
          )}

          {/* Deactivation confirmation modal */}
          {delclick && (
            <div className="modal-overlay-profile">
              <div className="modal-box-profile">
                <h3>Deactivate Account?</h3>
                <p>
                  Are you sure you want to deactivate your account? All your
                  personal data and test history will be permanently deleted.
                </p>
                <div className="modal-buttons-profile">
                  <button
                    onClick={() => setDelClick(false)}
                    className="modal-btn-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setDelClick(false)}
                    className="modal-btn-confirm"
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete chats confirmation modal */}
          {deleteChats && (
            <div className="modal-overlay-profile">
              <div className="modal-box-profile">
                <h3>Delete all chats?</h3>
                <p>
                  Are you sure? This will permanently erase all past chats and
                  feedback sessions with Yooki.
                </p>
                <div className="modal-buttons-profile">
                  <button
                    onClick={() => setDeleteChats(false)}
                    className="modal-btn-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteAllChats();
                      setDeleteChats(false);
                    }}
                    className="modal-btn-confirm"
                  >
                    Confirm Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* OTP Verification Modal for first-time Email / Phone */}
          {otpModal.isOpen && (
            <div className="modal-overlay-profile">
              <div className="modal-box-profile">
                <h3>
                  Verify {otpModal.type === "email" ? "Email Address" : "Phone Number"}
                </h3>
                <p>
                  Enter the 6-digit verification code sent to{" "}
                  <strong>
                    {otpModal.type === "email"
                      ? otpModal.value
                      : `+91 ${otpModal.value}`}
                  </strong>
                </p>
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="text"
                    className="profile-form-input"
                    value={otpModal.otp}
                    onChange={(e) =>
                      setOtpModal((prev) => ({
                        ...prev,
                        otp: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    style={{
                      textAlign: "center",
                      letterSpacing: "4px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                    autoFocus
                  />
                </div>
                {otpModal.error && (
                  <div
                    className="profile-error-alert"
                    style={{
                      marginBottom: 12,
                      color: otpModal.error.includes("sent") ? "#059669" : "#dc2626",
                      background: otpModal.error.includes("sent") ? "#ecfdf5" : "#fef2f2",
                      borderColor: otpModal.error.includes("sent") ? "#a7f3d0" : "#fecaca",
                    }}
                  >
                    {otpModal.error}
                  </div>
                )}
                <div className="modal-buttons-profile">
                  <button
                    onClick={() =>
                      setOtpModal({
                        isOpen: false,
                        type: null,
                        value: "",
                        otp: "",
                        loading: false,
                        error: "",
                      })
                    }
                    className="modal-btn-cancel"
                    disabled={otpModal.loading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyOtp}
                    className="profile-btn-primary"
                    disabled={otpModal.loading}
                  >
                    {otpModal.loading ? "Verifying..." : "Verify & Save"}
                  </button>
                </div>
                <div style={{ marginTop: 12 }}>
                  <button
                    onClick={handleResendOtp}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#0284c7",
                      fontSize: "13px",
                      fontFamily: "inherit",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                    disabled={otpModal.loading}
                  >
                    Didn't receive code? Resend OTP
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Policy / Terms Modal */}
          <PolicyModal
            isOpen={policyModal.isOpen}
            onClose={() =>
              setPolicyModal((prev) => ({ ...prev, isOpen: false }))
            }
            type={policyModal.type}
          />
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
